import type {
  LibraryDependency,
  LibraryItemView,
} from '@/lib/api/generated/model';
import { summarizeInstall, type PlannedDependency } from './summarizeInstall';

export interface InstallTreeNode {
  entry: PlannedDependency;
  /** Its index in the item's install list, which is the order an install settles in. */
  order: number;
  children: InstallTreeNode[];
}

export interface InstallTree {
  nodes: InstallTreeNode[];
  /**
   * Links the item's own steps make to workflows the library does not hold. The
   * service uses whatever the workspace has at that key (spec 108 research R5),
   * so the install list leaves them out; the tree names them so the chain is not
   * silently shorter than the outline says.
   */
  workspaceOwn: string[];
}

const id = (dep: LibraryDependency) => `${dep.kind}/${dep.key}`;

function catalogIndex(items: readonly LibraryItemView[]) {
  return new Map(items.map((i) => [`${i.kind}/${i.key}`, i]));
}

function requiresOf(item: LibraryItemView | undefined): LibraryDependency[] {
  return item?.requires ?? [];
}

/**
 * The workflows `item` links to itself, in install order (spec 109 research R1).
 *
 * `requires` is everything an install brings in, however deep, so a workflow
 * reached through another is in it too. What is left after dropping every
 * workflow some other listed workflow already brings in is exactly what the
 * item's own steps name — the prototype's transitive reduction, run over the
 * catalog the page already holds.
 */
export function directLinks(
  item: LibraryItemView,
  items: readonly LibraryItemView[],
): string[] {
  const byId = catalogIndex(items);
  const workflows = requiresOf(item).filter((dep) => dep.kind === 'workflow');
  return workflows
    .filter(
      (candidate) =>
        !workflows.some(
          (other) =>
            other.key !== candidate.key &&
            requiresOf(byId.get(id(other))).some(
              (dep) => id(dep) === id(candidate),
            ),
        ),
    )
    .map((dep) => dep.key);
}

/**
 * What installing `item` brings in, arranged by which workflow brings in what
 * (spec 109 FR-005, research R2).
 *
 * Walked in install order. A linked workflow sits under the deepest workflow
 * linking to it; an agent or skill under the first workflow, in that order,
 * that needs it. Every row of {@link summarizeInstall} appears exactly once and
 * keeps its plan, so the drawer's sentence counting what Install adds cannot
 * disagree with the tree above it.
 */
export function installTree(
  item: LibraryItemView,
  items: readonly LibraryItemView[],
  ownLinks: readonly string[] = [],
): InstallTree {
  const byId = catalogIndex(items);
  const install = requiresOf(item);
  const rows = new Map(
    summarizeInstall(item, items).rows.map((row) => [id(row.dep), row]),
  );
  const order = new Map(install.map((dep, index) => [id(dep), index]));
  const placed = new Set<string>();

  const node = (dep: LibraryDependency, children: InstallTreeNode[] = []) => {
    placed.add(id(dep));
    const entry = rows.get(id(dep));
    return entry === undefined
      ? []
      : [{ entry, order: order.get(id(dep)) ?? 0, children }];
  };

  const build = (owner: LibraryItemView): InstallTreeNode[] => {
    const own = requiresOf(owner);
    const children = new Set(directLinks(owner, items));
    // Anything a listed workflow brings in belongs further down its branch.
    const below = new Set(
      own
        .filter((dep) => dep.kind === 'workflow')
        .flatMap((dep) => requiresOf(byId.get(id(dep))).map(id)),
    );
    const nodes: InstallTreeNode[] = [];
    for (const dep of own) {
      if (placed.has(id(dep))) continue;
      const found = byId.get(id(dep));
      if (dep.kind === 'workflow' && found !== undefined) {
        if (!children.has(dep.key)) continue;
        placed.add(id(dep));
        nodes.push(...node(dep, build(found)));
      } else if (!below.has(id(dep))) {
        nodes.push(...node(dep));
      }
    }
    return nodes;
  };

  const nodes = build(item);
  // A catalog that disagrees with itself must still show everything once.
  for (const dep of install) {
    if (!placed.has(id(dep))) nodes.push(...node(dep));
  }

  const workspaceOwn = [...new Set(ownLinks)].filter(
    (key) => key !== item.key && !byId.has(`workflow/${key}`),
  );
  return { nodes, workspaceOwn };
}
